class CollectionTabGrid extends HTMLElement {
  connectedCallback() {
    this._cache = new Map(); // key: `${handle}-${page}` → { items: Node[], hasNext, total }
    this._ctrl = null; // AbortController for in-flight fetch
    this._handle = null;
    this._page = 1;
    this._hasNext = false;
    this._busy = false;

    this._sentinel = this.querySelector('.ctg__sentinel');
    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this._loadNextPage();
      },
      { rootMargin: '0px 0px 700px 0px' }
    );

    this._initFromDOM();
    this._bindTabs();

    // Infinite scroll off in theme editor — tab switching still works
    if (window.Shopify?.designMode) return;
    this._observer.observe(this._sentinel);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._ctrl?.abort();
  }

  _initFromDOM() {
    const wrap = this.querySelector('.ctg__grid-wrap');
    if (!wrap) return;
    this._handle = wrap.dataset.collection;
    this._hasNext = wrap.dataset.hasNext === 'true';
    this._page = 1;
    // Cache server-rendered page 1
    const items = [...this.querySelectorAll('.ctg__item')];
    this._cache.set(`${this._handle}-1`, {
      items: items.map((n) => n.cloneNode(true)),
      hasNext: this._hasNext,
      total: parseInt(wrap.dataset.total || '0', 10)
    });
  }

  _bindTabs() {
    this.querySelectorAll('[role="tab"]').forEach((btn) => btn.addEventListener('click', () => this._switchTab(btn)));
  }

  async _switchTab(btn) {
    const handle = btn.dataset.collection;
    if (handle === this._handle || this._busy) return;

    // Update ARIA + visual state
    this.querySelectorAll('[role="tab"]').forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-selected', String(b === btn));
    });

    // Cancel previous fetch
    this._ctrl?.abort();
    this._handle = handle;
    this._page = 1;

    // Reset observer — will be re-started by _updateSentinel
    this._observer.disconnect();

    this._scrollToGrid();

    const cacheKey = `${handle}-1`;
    if (this._cache.has(cacheKey)) {
      const cached = this._cache.get(cacheKey);
      this._hasNext = cached.hasNext;
      this._renderReplace(cached.items);
      this._updateSentinel();
      return;
    }

    this._setLoading(true);
    await this._fetch(handle, 1, 'replace');
    this._setLoading(false);
  }

  _scrollToGrid() {
    const tabsWrap = this.querySelector('.ctg__tablist-wrap');
    const gridWrap = this.querySelector('.ctg__grid-wrap');
    if (!gridWrap) return;
    const tabsBottom = tabsWrap ? tabsWrap.getBoundingClientRect().bottom : 0;
    const gridTop = gridWrap.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: gridTop - tabsBottom, behavior: 'smooth' });
  }

  async _loadNextPage() {
    if (!this._hasNext || this._busy) return;
    this._page++;
    const spinner = this.querySelector('.ctg__append-spinner');
    spinner.hidden = false;
    await this._fetch(this._handle, this._page, 'append');
    spinner.hidden = true;
  }

  async _fetch(handle, page, mode) {
    this._busy = true;
    this._ctrl = new AbortController();
    const fetchSection = this.dataset.fetchSection; // "tab-products-fetch"
    const url = `/collections/${handle}?section_id=${fetchSection}&page=${page}`;

    try {
      const res = await fetch(url, { signal: this._ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = doc.querySelector('.cpf__grid');
      if (!grid) throw new Error('No .cpf__grid in response');

      const items = [...doc.querySelectorAll('.ctg__item')];
      const hasNext = grid.dataset.hasNext === 'true';
      const total = parseInt(grid.dataset.total || '0', 10);

      this._cache.set(`${handle}-${page}`, {
        items: items.map((n) => n.cloneNode(true)),
        hasNext,
        total
      });
      this._hasNext = hasNext;

      if (mode === 'replace') {
        this._renderReplace(items);
      } else {
        this._renderAppend(items);
      }
      this._updateSentinel();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[collection-tab-grid] fetch error:', err);
      this._showError();
      this._page = Math.max(1, this._page - 1);
    } finally {
      this._busy = false;
    }
  }

  _renderReplace(items) {
    const list = this.querySelector('.ctg__grid');
    list.replaceChildren(...items.map((n) => n.cloneNode(true)));
    const wrap = this.querySelector('.ctg__grid-wrap');
    if (wrap) {
      wrap.dataset.collection = this._handle;
      wrap.dataset.page = this._page;
      wrap.dataset.hasNext = this._hasNext;
    }
  }

  _renderAppend(items) {
    const list = this.querySelector('.ctg__grid');
    items.forEach((n) => list.appendChild(n.cloneNode(true)));
  }

  _updateSentinel() {
    const done = !this._hasNext;

    if (done || window.Shopify?.designMode) {
      this._observer.disconnect();
    } else {
      this._observer.disconnect();
      this._observer.observe(this._sentinel);
    }
  }

  _setLoading(visible) {
    const overlay = this.querySelector('.ctg__overlay');
    if (overlay) overlay.hidden = !visible;
  }

  _showError() {
    const existing = this.querySelector('.ctg__error');
    if (existing) existing.remove();
    const gridWrap = this.querySelector('.ctg__grid-wrap');
    const msg = document.createElement('p');
    msg.className = 'ctg__error';
    msg.textContent = 'Failed to load products. Please try again.';
    gridWrap?.insertAdjacentElement('afterend', msg);
  }
}

customElements.define('collection-tab-grid', CollectionTabGrid);
